import axios from 'axios';
import { API_URL } from '../config';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const streetlearnApi = {
  // Learners
  getLearners: async (params = {}) => {
    const res = await axios.get(`${API_URL}streetlearn/learners`, {
      ...getAuthHeaders(),
      params,
    });
    return res.data;
  },

  getLearnerById: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/learners/${id}`, getAuthHeaders());
    return res.data;
  },

  getLearnerProgress: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/learners/${id}/progress`, getAuthHeaders());
    return res.data;
  },

  getLearnerAttempts: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/learners/${id}/attempts`, getAuthHeaders());
    return res.data;
  },

  getExamAttempt: async (attemptId) => {
    const res = await axios.get(`${API_URL}streetlearn/exam-attempts/${attemptId}`, getAuthHeaders());
    return res.data;
  },

  updateLearner: async (id, data) => {
    const res = await axios.patch(`${API_URL}streetlearn/learners/${id}`, data, getAuthHeaders());
    return res.data;
  },

  logoutEverywhere: async (id) => {
    const res = await axios.post(`${API_URL}streetlearn/learners/${id}/logout-everywhere`, {}, getAuthHeaders());
    return res.data;
  },

  // Entitlements
  grantEntitlement: async (learnerId, data) => {
    const res = await axios.post(`${API_URL}streetlearn/learners/${learnerId}/entitlements`, data, getAuthHeaders());
    return res.data;
  },

  revokeEntitlement: async (entitlementId, data) => {
    const res = await axios.delete(`${API_URL}streetlearn/entitlements/${entitlementId}`, {
      ...getAuthHeaders(),
      data,
    });
    return res.data;
  },

  bulkGrantEntitlements: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/entitlements/bulk`, data, getAuthHeaders());
    return res.data;
  },

  // Subscriber Linking
  getSubscriberSuggestions: async (learnerId) => {
    const res = await axios.get(`${API_URL}streetlearn/learners/${learnerId}/subscriber-suggestions`, getAuthHeaders());
    return res.data;
  },

  linkSubscriber: async (learnerId, data) => {
    const res = await axios.put(`${API_URL}streetlearn/learners/${learnerId}/subscriber`, data, getAuthHeaders());
    return res.data;
  },

  // Orders
  getOrders: async (params = {}) => {
    const res = await axios.get(`${API_URL}streetlearn/orders`, {
      ...getAuthHeaders(),
      params,
    });
    return res.data;
  },

  getOrderById: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/orders/${id}`, getAuthHeaders());
    return res.data;
  },

  // Certificates
  getCertificates: async (params = {}) => {
    const res = await axios.get(`${API_URL}streetlearn/certificates`, {
      ...getAuthHeaders(),
      params,
    });
    return res.data;
  },

  updateCertificate: async (id, data) => {
    const res = await axios.patch(`${API_URL}streetlearn/certificates/${id}`, data, getAuthHeaders());
    return res.data;
  },

  revokeCertificate: async (id, data) => {
    const res = await axios.post(`${API_URL}streetlearn/certificates/${id}/revoke`, data, getAuthHeaders());
    return res.data;
  },

  restoreCertificate: async (id) => {
    const res = await axios.post(`${API_URL}streetlearn/certificates/${id}/restore`, {}, getAuthHeaders());
    return res.data;
  },

  // Questions
  getQuestions: async (params = {}) => {
    const res = await axios.get(`${API_URL}streetlearn/questions`, {
      ...getAuthHeaders(),
      params,
    });
    return res.data;
  },

  getQuestionById: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/questions/${id}`, getAuthHeaders());
    return res.data;
  },

  createQuestion: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/questions`, data, getAuthHeaders());
    return res.data;
  },

  updateQuestion: async (id, data) => {
    const res = await axios.patch(`${API_URL}streetlearn/questions/${id}`, data, getAuthHeaders());
    return res.data;
  },

  deleteQuestion: async (id) => {
    const res = await axios.delete(`${API_URL}streetlearn/questions/${id}`, getAuthHeaders());
    return res.data;
  },

  bulkStatusQuestions: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/questions/bulk-status`, data, getAuthHeaders());
    return res.data;
  },

  getQuestionPoolSummary: async () => {
    const res = await axios.get(`${API_URL}streetlearn/questions/pool-summary`, getAuthHeaders());
    return res.data;
  },

  getQuestionStats: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/questions/${id}/stats`, getAuthHeaders());
    return res.data;
  },

  importQuestions: async (formData, dryRun = true) => {
    const res = await axios.post(
      `${API_URL}streetlearn/questions/import?dry_run=${dryRun ? 1 : 0}`,
      formData,
      {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return res.data;
  },

  // Exam Configs
  getExamConfigs: async () => {
    const res = await axios.get(`${API_URL}streetlearn/exam-configs`, getAuthHeaders());
    return res.data;
  },

  getExamConfigByLevel: async (levelId) => {
    const res = await axios.get(`${API_URL}streetlearn/exam-configs/${levelId}`, getAuthHeaders());
    return res.data;
  },

  updateExamConfig: async (levelId, data) => {
    const res = await axios.put(`${API_URL}streetlearn/exam-configs/${levelId}`, data, getAuthHeaders());
    return res.data;
  },

  // Curriculum: Tiers & Levels
  getTiers: async () => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/tiers`, getAuthHeaders());
    return res.data;
  },

  getLevels: async () => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/levels`, getAuthHeaders());
    return res.data;
  },

  getLevelById: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/levels/${id}`, getAuthHeaders());
    return res.data;
  },

  updateLevel: async (id, data) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/levels/${id}`, data, getAuthHeaders());
    return res.data;
  },

  // Curriculum: Modules
  getModules: async (levelId) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/modules`, {
      ...getAuthHeaders(),
      params: levelId ? { level_id: levelId } : {},
    });
    return res.data;
  },

  createModule: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/modules`, data, getAuthHeaders());
    return res.data;
  },

  updateModule: async (id, data) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/modules/${id}`, data, getAuthHeaders());
    return res.data;
  },

  deleteModule: async (id) => {
    const res = await axios.delete(`${API_URL}streetlearn/curriculum/modules/${id}`, getAuthHeaders());
    return res.data;
  },

  reorderModules: async (items) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/modules/reorder`, { items }, getAuthHeaders());
    return res.data;
  },

  // Curriculum: Lessons
  getLessonTypes: async () => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/lesson-types`, getAuthHeaders());
    return res.data;
  },

  getLessons: async (params = {}) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/lessons`, {
      ...getAuthHeaders(),
      params,
    });
    return res.data;
  },

  getLessonById: async (id) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/lessons/${id}`, getAuthHeaders());
    return res.data;
  },

  createLesson: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/lessons`, data, getAuthHeaders());
    return res.data;
  },

  updateLesson: async (id, data) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/lessons/${id}`, data, getAuthHeaders());
    return res.data;
  },

  deleteLesson: async (id) => {
    const res = await axios.delete(`${API_URL}streetlearn/curriculum/lessons/${id}`, getAuthHeaders());
    return res.data;
  },

  reorderLessons: async (items) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/lessons/reorder`, { items }, getAuthHeaders());
    return res.data;
  },

  // Curriculum: Lesson Content & Versions
  getLessonVersions: async (lessonId) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/lessons/${lessonId}/versions`, getAuthHeaders());
    return res.data;
  },

  createLessonVersion: async (lessonId, data) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/lessons/${lessonId}/versions`, data, getAuthHeaders());
    return res.data;
  },

  getLessonContent: async (contentId) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/versions/${contentId}`, getAuthHeaders());
    return res.data;
  },

  updateLessonContent: async (contentId, data) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/versions/${contentId}`, data, getAuthHeaders());
    return res.data;
  },

  submitVersionForReview: async (contentId) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/versions/${contentId}/submit-review`, {}, getAuthHeaders());
    return res.data;
  },

  approveLessonVersion: async (contentId, data = {}) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/versions/${contentId}/approve`, data, getAuthHeaders());
    return res.data;
  },

  rejectLessonVersion: async (contentId, data = {}) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/versions/${contentId}/reject`, data, getAuthHeaders());
    return res.data;
  },

  publishLessonVersion: async (contentId) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/versions/${contentId}/publish`, {}, getAuthHeaders());
    return res.data;
  },

  // Curriculum: Downloads
  getLessonDownloads: async (lessonId) => {
    const res = await axios.get(`${API_URL}streetlearn/curriculum/lessons/${lessonId}/downloads`, getAuthHeaders());
    return res.data;
  },

  createDownload: async (data) => {
    const res = await axios.post(`${API_URL}streetlearn/curriculum/downloads`, data, getAuthHeaders());
    return res.data;
  },

  updateDownload: async (id, data) => {
    const res = await axios.put(`${API_URL}streetlearn/curriculum/downloads/${id}`, data, getAuthHeaders());
    return res.data;
  },

  deleteDownload: async (id) => {
    const res = await axios.delete(`${API_URL}streetlearn/curriculum/downloads/${id}`, getAuthHeaders());
    return res.data;
  },

  // Analytics
  getAnalyticsOverview: async () => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/overview`, getAuthHeaders());
    return res.data;
  },

  getAnalyticsActivityTrends: async (days = 30) => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/activity-trends`, {
      ...getAuthHeaders(),
      params: { days },
    });
    return res.data;
  },

  getAnalyticsFunnel: async () => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/funnel`, getAuthHeaders());
    return res.data;
  },

  getAnalyticsExams: async () => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/exams`, getAuthHeaders());
    return res.data;
  },

  getAnalyticsToughestLessons: async (limit = 10) => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/toughest-lessons`, {
      ...getAuthHeaders(),
      params: { limit },
    });
    return res.data;
  },

  getAnalyticsQuestionHealth: async () => {
    const res = await axios.get(`${API_URL}streetlearn/analytics/question-health`, getAuthHeaders());
    return res.data;
  },
};

